/**
 * 共通整形ユーティリティ（Unit 006 で集約）
 *
 * 時間整形は画面ごとに出力仕様が異なっていた（Dashboard=単位なし/∞、Upload=toFixed/無制限、
 * Billing=時間/無制限）。共通コア（時間→分換算）を1つ持ち、表示差分をオプションで吸収することで、
 * 各画面の現出力を維持したまま重複を解消する。
 */

export interface FormatHoursOptions {
  /** 1時間以上・1時間未満の数値に付ける単位（例: '時間'）。既定は単位なし */
  unit?: string
  /** toFixed の桁数。whole=1時間以上、fraction=1時間未満。未指定なら toFixed しない */
  toFixedDigits?: { whole?: number; fraction?: number }
  /** hours === -1（無制限）のラベル。既定 '無制限' */
  unlimitedLabel?: string
  /**
   * 1時間未満の枠を「N分」の分のみ表記にする（既定 false）。
   * 対象は `0 <= hours < 1` の枠のみ（現行プラン値は 0.25 等）。1時間以上・無制限は影響を受けない。
   * 消費は Unit 005（Billing の残時間表示）専用。秒起点の可読化には `formatDuration` を使うこと。
   * 注: `Math.round(hours * 60)` により hours が 1 に極めて近い場合（0.9917〜）「60分」を出力しうるが、
   * その値は現行データに存在しないため許容し、1時間への丸め上げは行わない。
   */
  minutesOnlyBelowOneHour?: boolean
}

/**
 * 時間を「N時間」「N時間（M分）」等に整形する共通関数。
 * - hours === -1: 無制限ラベル
 * - hours >= 1: 整数時間（whole 指定時は toFixed）
 * - hours < 1: 分換算を併記（fraction 指定時は toFixed）。
 *   minutesOnlyBelowOneHour=true のときは「N分」の分のみ表記。
 */
export function formatHours(hours: number, options: FormatHoursOptions = {}): string {
  const { unit = '', toFixedDigits, unlimitedLabel = '無制限', minutesOnlyBelowOneHour = false } = options

  if (hours === -1) return unlimitedLabel

  if (hours >= 1) {
    const value = toFixedDigits?.whole != null ? hours.toFixed(toFixedDigits.whole) : `${hours}`
    return `${value}${unit}`
  }

  const minutes = Math.round(hours * 60)
  if (minutesOnlyBelowOneHour) return `${Math.max(0, minutes)}分`

  const value = toFixedDigits?.fraction != null ? hours.toFixed(toFixedDigits.fraction) : `${hours}`
  return `${value}${unit}（${minutes}分）`
}

/**
 * 使用時間を「人が読む」表記へ整形する共通関数（画面をまたぐ使用時間表示の単一の真実源）。
 *
 * 以前は画面ごとに出力がばらついていた（Dashboard=「0.03（2分）」「∞」、Profile=「0.0」）。
 * 0.03 のような内部の小数をそのまま見せても意味が伝わらないため、小数を排除して自然な単位に統一する。
 * - hours === -1（無制限センチネル）: 「無制限」
 * - 1時間未満: 「2分」（分のみ）
 * - 1時間以上: 「1時間30分」（端数0分なら「2時間」）
 *
 * 注: プランの残時間表示（Billing）は `formatHours` が別仕様を持つため、そちらは変更しない。
 */
export function formatUsageHours(hours: number): string {
  if (hours === -1) return '無制限'

  const totalMinutes = Math.round(hours * 60)
  if (totalMinutes < 60) return `${Math.max(0, totalMinutes)}分`

  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return m === 0 ? `${h}時間` : `${h}時間${m}分`
}

/**
 * 特典コードの付与内容を部分ごとの表記にする（例: ['1時間', '3回']）。0 の部分は含めない。
 * つなぎ方は呼び出し側が決める（完了の文言は「と」、一覧は「・」）。
 */
export function formatGrantParts(hours: number, sessions: number): string[] {
  const parts: string[] = []
  if (hours > 0) parts.push(formatUsageHours(hours))
  if (sessions > 0) parts.push(`${sessions}回`)
  return parts
}

/**
 * 書き起こしの見出しに使う表示名。
 * ユーザーがリネームしたタイトルを優先し、未設定（null/空）なら元のファイル名へフォールバックする。
 */
export function transcriptionTitle(t: { title?: string | null; audio_filename: string }): string {
  const title = t.title?.trim()
  return title ? title : t.audio_filename
}

/**
 * プラン種別を日本語ラベルへ変換する共通関数（プラン表記の単一の真実源）。
 * - free→無料 / lite→ライト / standard→スタンダード / unlimited→無制限（大文字小文字は同一視）
 * - 既知集合に無い値は入力をそのまま返す（生値フォールバック）
 * - null/undefined/非文字列/空文字は '' を返す（消費側のロード前レンダリングをクラッシュさせない）
 *
 * 署名は実入力域（ロード前は null/undefined が来うる）を型で表現するため string | null | undefined。
 */
export function planLabel(planType: string | null | undefined): string {
  if (typeof planType !== 'string' || planType === '') return ''

  const labels: Record<string, string> = {
    free: '無料',
    lite: 'ライト',
    standard: 'スタンダード',
    unlimited: '無制限',
  }
  return labels[planType.toLowerCase()] ?? planType
}

/** ISO8601 の日時を「M月D日」へ整形する（プラン画面の更新日・満了日の表示用） */
export function formatMonthDay(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' })
}

/**
 * 有料プラン契約中（解約予約なし）の次回更新日の表示文言。該当しない・日付が未取得なら null。
 * 日付は current_period_end（決済側の期間終了日の写し）のみを使い、billing_cycle_end は使わない。
 */
export function nextRenewalLabel(plan: {
  plan_type: string
  cancel_at_period_end?: boolean
  current_period_end?: string | null
}): string | null {
  if (plan.plan_type === 'free' || plan.cancel_at_period_end || !plan.current_period_end) return null
  return `次回更新日 ${formatMonthDay(plan.current_period_end)}（自動更新）`
}

/**
 * 処理秒数を可読な時間表現へ整形する共通関数。
 * - null / NaN / 負値 / 非有限値 は '-'（呼び出し側で null 分岐しない仕様）
 * - 四捨五入で整数秒化してから 3 段階表示
 *   - 60秒未満: 「N秒」
 *   - 60秒以上〜3600秒未満: 「N分M秒」
 *   - 3600秒以上: 「N時間M分」
 * - 閾値は下限を含む（60秒→「1分0秒」、3600秒→「1時間0分」）
 */
export function formatDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '-'

  const total = Math.round(seconds)

  if (total < 60) return `${total}秒`
  if (total < 3600) return `${Math.floor(total / 60)}分${total % 60}秒`

  const hrs = Math.floor(total / 3600)
  const mins = Math.floor((total % 3600) / 60)
  return `${hrs}時間${mins}分`
}

/** 秒を mm:ss または h:mm:ss に整形（書き起こしセグメントのタイムスタンプ） */
export function formatTimestamp(seconds: number): string {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * 特典コードを表示形（XXXX-XXXX-XXXX）に整形する。
 *
 * 変換は表示方向の一方向のみ。逆変換（ハイフン除去・大文字化）は持たない——
 * 入力の正規化は DB 関数 redeem_code の責務で、画面側にも置くと規則の置き場所が2つになる。
 * 桁数が想定外でも例外にせず4桁区切りで返す（表示だけの関数で、落ちる価値がない）。
 */
export function formatRedemptionCode(code: string): string {
  return code.replace(/(.{4})(?=.)/g, '$1-')
}

/** バイト数を B / KB / MB に整形 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}
